import React, { Component } from "react";
import HomePV from "./HomePV";
import AuthA from "../../store/actions/AuthA";
import QuarterA from "../../store/actions/QuarterA";
import { connect } from "react-redux";

import axios from "axios";
import { fetchData } from "../../utils/fetch-data";
import find from "lodash/find";
import { getCurrentQuarter } from "../../utils/quarterUtils";
import { resolveGradingScheme } from "../../utils/gradingScheme";
import { awaitsUser } from "../../utils/clubWorkflow";

const _ = {
  find: find,
};

class HomePC extends Component {
  constructor(props) {
    super(props);
    this.state = {
      perSubjectSummary: [],
      perSectionSummary: [],
      perLevelSummary: [],
      perQuarterSummary: [],
      perSubjectSummaryConducts: [],
      perSectionSummaryConducts: [],
      perLevelSummaryConducts: [],
      perQuarterSummaryConducts: [],
      schoolYears: [],
      currentSchoolYear: "",
      schoolYearId: "",
      loadedSubject: false,
      loadedClass: false,
      loadedLevel: false,
      loadedQuarter: false,
      activeButton: "subject",
      myClubs: [],

      details: [],
      detailsAdmin: [],
      searchKey: "",
      activePage: 1,
      itemPerPage: 5,
      selectedIndex: 0,
      openConfirm: false,
      link: "",
      teacherId: "",
      
      // Loading states
      loadingAdmin: false,
      loadingLevel: false,
      loadingData: false,
      loadingQuarterSummary: false,
      quarterSummaryError: null,
      quarterSummaryRetryCount: 0,
      loadingQuarterSummaryConducts: false,
      quarterSummaryConductsError: null,
      quarterSummaryConductsRetryCount: 0,
      loadingLevelSummary: false,
      levelSummaryError: null,
      levelSummaryRetryCount: 0,
    };

    // Bind methods
    this.onSearchChange = this.onSearchChange.bind(this);
    this.handlePaginationChange = this.handlePaginationChange.bind(this);
    
    // Request management
    this.abortControllers = new Map();
    this.requestQueue = new Set();
    
    // Debounced functions
    this.debouncedRefreshData = this.debounceFunction(this.refreshDataForUserType.bind(this), 300);
  }

  // Debounce utility function
  debounceFunction = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Centralized request handler with abort control and timeout
  makeRequest = async (endpoint, requestKey, useCache = true, timeoutMs = 45000) => {
    // Generate cache key
    const cacheKey = `${requestKey}-${this.props.schoolYearId}-${this.props.currentQuarter}`;
    
    // Check cache first
    if (useCache && this.cachedData && this.cachedData[cacheKey]) {
      console.log(`📋 Using cached data for ${requestKey}`);
      return this.cachedData[cacheKey];
    }

    // Cancel existing request if pending
    if (this.abortControllers.has(requestKey)) {
      console.log(`🚫 Canceling existing request for ${requestKey}`);
      this.abortControllers.get(requestKey).abort();
    }

    // Create new abort controller
    const controller = new AbortController();
    this.abortControllers.set(requestKey, controller);
    
    // Set up timeout
    const timeoutId = setTimeout(() => {
      console.log(`⏰ Request timeout after ${timeoutMs}ms for ${requestKey}`);
      controller.abort();
    }, timeoutMs);
    
    try {
      console.log(`🚀 Making request: ${requestKey} (timeout: ${timeoutMs}ms)`);
      console.time(`Request-${requestKey}`);
      
      const response = await fetchData(endpoint, { 
        signal: controller.signal 
      });
      
      console.timeEnd(`Request-${requestKey}`);
      clearTimeout(timeoutId);
      
      if (response && !controller.signal.aborted) {
        console.log(`✅ Request successful: ${requestKey}`);
        // Cache the result
        if (!this.cachedData) this.cachedData = {};
        this.cachedData[cacheKey] = response.data;
        
        return response.data;
      }
      console.log(`⚠️ Request returned no data: ${requestKey}`);
      return null;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        console.log(`❌ Request aborted: ${requestKey}`);
        // Create a more specific timeout error
        const isTimeout = controller.signal.aborted;
        if (isTimeout) {
          const timeoutError = new Error(`Request timeout after ${timeoutMs}ms`);
          timeoutError.name = 'TimeoutError';
          timeoutError.requestKey = requestKey;
          throw timeoutError;
        }
      } else {
        console.error(`❌ Request failed: ${requestKey}`, error.message);
      }
      throw error;
    } finally {
      this.abortControllers.delete(requestKey);
    }
  };

  // Optimized API methods
  getSubjectSummary = async () => {
    try {
      const data = await this.makeRequest(
        `/sectionSubjects/summary/grades/subject?teacherId=${this.props.userId}&schoolYearId=${this.props.schoolYearId}&quarter=${this.props.currentQuarter}`,
        'subjectSummary'
      );
      if (data) {
        this.setState(prevState => ({
          perSubjectSummary: data,
          loadedSubject: true,
        }));
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading subject summary:', error);
      }
    }
  };

  getClassSummary = async () => {
    try {
      const data = await this.makeRequest(
        `/sectionSubjects/summary/grades/class?teacherId=${this.props.userId}&schoolYearId=${this.props.schoolYearId}&quarter=${this.props.currentQuarter}`,
        'classSummary'
      );
      if (data) {
        this.setState(prevState => ({
          perSectionSummary: data,
          loadedClass: true,
        }));
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading class summary:', error);
      }
    }
  };

  getLevelSummary = async (retryCount = 0) => {
    const maxRetries = 3;
    const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
    
    // Validate required props before making request
    const { levelType, schoolYearId, currentQuarter } = this.props;
    
    if (!levelType || !schoolYearId || !currentQuarter) {
      console.log('⚠️ Level summary skipped - missing required props:', {
        levelType, schoolYearId, currentQuarter
      });
      return;
    }
    
    try {
      console.log(`🔄 Getting level summary (attempt ${retryCount + 1}/${maxRetries + 1})`);
      console.log('📋 Props for level request:', { levelType, schoolYearId, currentQuarter });
      
      // Show loading state for level summary specifically
      if (retryCount === 0) {
        this.setState({ 
          loadedLevel: false,
          loadingLevelSummary: true,
          levelSummaryError: null 
        });
        
        // Show warning after 10 seconds
        setTimeout(() => {
          if (this.state.loadingLevelSummary && !this.state.levelSummaryError) {
            console.log('⚠️ Level summary taking longer than expected...');
            this.setState({
              levelSummaryError: 'Level summary is taking longer than usual (10+ seconds). This may be a backend issue. Please wait or try refreshing.'
            });
          }
        }, 10000);
      }
      
      const data = await this.makeRequest(
        `/sectionSubjects/summary/grades/level?levelType=${levelType}&schoolYearId=${schoolYearId}&quarter=${currentQuarter}&_t=${Date.now()}`,
        'levelSummary',
        false, // Disable cache for this endpoint
        45000 // 45 second timeout
      );
      
      if (data) {
        console.log('✅ Level summary loaded successfully');
        this.setState({
          perLevelSummary: data,
          loadedLevel: true,
          loadingLevelSummary: false,
          levelSummaryError: null,
          levelSummaryRetryCount: 0
        });
      } else {
        throw new Error('No data returned from level summary endpoint');
      }
    } catch (error) {
      console.error(`❌ Level summary attempt ${retryCount + 1} failed:`, error.message);
      
      // Check if we should retry
      const shouldRetry = retryCount < maxRetries && 
                         (error.name === 'TimeoutError' || 
                          error.name === 'AbortError' || 
                          error.message.includes('Network Error'));
      
      if (shouldRetry) {
        console.log(`⏳ Retrying level summary in ${retryDelay}ms...`);
        this.setState({ 
          levelSummaryRetryCount: retryCount + 1,
          levelSummaryError: `Request failed, retrying in ${retryDelay/1000}s... (${retryCount + 1}/${maxRetries})`
        });
        
        setTimeout(() => {
          this.getLevelSummary(retryCount + 1);
        }, retryDelay);
      } else {
        // Final failure - stop retrying
        console.error('💥 Level summary failed after all retries');
        this.setState({
          loadingLevelSummary: false,
          loadedLevel: false,
          levelSummaryError: `Failed to load level summary data after ${maxRetries + 1} attempts. ${error.message}`,
          levelSummaryRetryCount: 0
        });
      }
    }
  };

  getQuarterSummary = async (retryCount = 0) => {
    const maxRetries = 3;
    const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
    
    // Validate required props before making request
    const { userType, schoolYearId, currentQuarter } = this.props;
    
    if (!userType || !schoolYearId || !currentQuarter) {
      console.log('⚠️ Quarter summary skipped - missing required props:', {
        userType, schoolYearId, currentQuarter
      });
      return;
    }
    
    try {
      console.log(`🔄 Getting quarter summary (attempt ${retryCount + 1}/${maxRetries + 1})`);
      console.log('📋 Props for request:', { userType, schoolYearId, currentQuarter });
      
      // Show loading state for admin grades specifically
      if (retryCount === 0) {
        this.setState({ 
          loadedQuarter: false,
          loadingQuarterSummary: true,
          quarterSummaryError: null 
        });
        
        // Show warning after 10 seconds
        setTimeout(() => {
          if (this.state.loadingQuarterSummary && !this.state.quarterSummaryError) {
            console.log('⚠️ Admin grades taking longer than expected...');
            this.setState({
              quarterSummaryError: 'Admin grades is taking longer than usual (10+ seconds). This is a known backend issue. Please wait or try refreshing.'
            });
          }
        }, 10000);
      }
      
      const data = await this.makeRequest(
        `/sectionSubjects/summary/grades/admin?userType=${userType}&schoolYearId=${schoolYearId}&quarter=${currentQuarter}&_t=${Date.now()}`,
        'quarterSummary',
        false, // Disable cache for this problematic endpoint
        45000 // 45 second timeout for this critical request
      );
      
      if (data) {
        console.log('✅ Quarter summary loaded successfully');
        this.setState({
          perQuarterSummary: data,
          loadedQuarter: true,
          loadingQuarterSummary: false,
          quarterSummaryError: null,
          quarterSummaryRetryCount: 0
        });
      } else {
        throw new Error('No data returned from quarter summary endpoint');
      }
    } catch (error) {
      console.error(`❌ Quarter summary attempt ${retryCount + 1} failed:`, error.message);
      
      // Check if we should retry
      const shouldRetry = retryCount < maxRetries && 
                         (error.name === 'TimeoutError' || 
                          error.name === 'AbortError' || 
                          error.message.includes('Network Error'));
      
      if (shouldRetry) {
        console.log(`⏳ Retrying quarter summary in ${retryDelay}ms...`);
        this.setState({ 
          quarterSummaryRetryCount: retryCount + 1,
          quarterSummaryError: `Request failed, retrying in ${retryDelay/1000}s... (${retryCount + 1}/${maxRetries})`
        });
        
        setTimeout(() => {
          this.getQuarterSummary(retryCount + 1);
        }, retryDelay);
      } else {
        // Final failure - stop retrying
        console.error('💥 Quarter summary failed after all retries');
        this.setState({
          loadingQuarterSummary: false,
          loadedQuarter: false,
          quarterSummaryError: `Failed to load admin grades data after ${maxRetries + 1} attempts. ${error.message}`,
          quarterSummaryRetryCount: 0
        });
      }
    }
  };

  getSectionLevelSummary = async () => {
    this.setState({ loadingLevel: true });
    try {
      const data = await this.makeRequest(
        `/sections/details/level?levelType=${this.props.levelType}&schoolYearId=${this.props.schoolYearId}&quarter=${this.props.currentQuarter}`,
        'sectionLevelSummary'
      );
      if (data) {
        this.setState(prevState => ({
          details: data.details || data,
          loadingLevel: false,
        }));
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading section level summary:', error);
      }
      this.setState({ loadingLevel: false });
    }
  };

  getSectionAdminSummary = async () => {
    this.setState({ loadingAdmin: true });
    try {
      const data = await this.makeRequest(
        `/sections/details/admin?userType=${this.props.userType}&schoolYearId=${this.props.schoolYearId}&quarter=${this.props.currentQuarter}`,
        'sectionAdminSummary'
      );
      if (data) {
        this.setState(prevState => ({
          detailsAdmin: data.details || data,
          loadingAdmin: false,
        }));
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading section admin summary:', error);
      }
      this.setState({ loadingAdmin: false });
    }
  };

  // Conduct methods (simplified for brevity - apply same pattern)
  getSubjectSummaryConducts = async () => {
    try {
      const data = await this.makeRequest(
        `/sectionSubjects/summary/conducts/subject?teacherId=${this.props.userId}&schoolYearId=${this.props.schoolYearId}&quarter=${this.props.currentQuarter}`,
        'subjectConducts'
      );
      if (data) {
        this.setState({ perSubjectSummaryConducts: data });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading subject conducts:', error);
      }
    }
  };

  getClassSummaryConducts = async () => {
    try {
      const data = await this.makeRequest(
        `/sectionSubjects/summary/conducts/class?teacherId=${this.props.userId}&schoolYearId=${this.props.schoolYearId}&quarter=${this.props.currentQuarter}`,
        'classConducts'
      );
      if (data) {
        this.setState({ perSectionSummaryConducts: data });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading class conducts:', error);
      }
    }
  };

  getLevelSummaryConducts = async () => {
    try {
      const data = await this.makeRequest(
        `/sectionSubjects/summary/conducts/level?levelType=${this.props.levelType}&schoolYearId=${this.props.schoolYearId}&quarter=${this.props.currentQuarter}`,
        'levelConducts'
      );
      if (data) {
        this.setState({ perLevelSummaryConducts: data });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading level conducts:', error);
      }
    }
  };

  getQuarterSummaryConducts = async (retryCount = 0) => {
    const maxRetries = 3;
    const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
    
    // Validate required props before making request
    const { userType, schoolYearId, currentQuarter } = this.props;
    
    if (!userType || !schoolYearId || !currentQuarter) {
      console.log('⚠️ Quarter conducts skipped - missing required props:', {
        userType, schoolYearId, currentQuarter
      });
      return;
    }
    
    try {
      console.log(`🔄 Getting quarter conducts (attempt ${retryCount + 1}/${maxRetries + 1})`);
      console.log('📋 Props for conducts request:', { userType, schoolYearId, currentQuarter });
      
      // Show loading state for admin conducts specifically
      if (retryCount === 0) {
        this.setState({ 
          loadingQuarterSummaryConducts: true,
          quarterSummaryConductsError: null 
        });
        
        // Show warning after 10 seconds
        setTimeout(() => {
          if (this.state.loadingQuarterSummaryConducts && !this.state.quarterSummaryConductsError) {
            console.log('⚠️ Admin conducts taking longer than expected...');
            this.setState({
              quarterSummaryConductsError: 'Admin conducts is taking longer than usual (10+ seconds). This is a known backend issue. Please wait or try refreshing.'
            });
          }
        }, 10000);
      }
      
      const data = await this.makeRequest(
        `/sectionSubjects/summary/conducts/admin?userType=${userType}&schoolYearId=${schoolYearId}&quarter=${currentQuarter}&_t=${Date.now()}`,
        'quarterConducts',
        false, // Disable cache for this problematic endpoint
        45000 // 45 second timeout for this critical request
      );
      
      if (data) {
        console.log('✅ Quarter conducts loaded successfully');
        this.setState({
          perQuarterSummaryConducts: data,
          loadingQuarterSummaryConducts: false,
          quarterSummaryConductsError: null,
          quarterSummaryConductsRetryCount: 0
        });
      } else {
        throw new Error('No data returned from quarter conducts endpoint');
      }
    } catch (error) {
      console.error(`❌ Quarter conducts attempt ${retryCount + 1} failed:`, error.message);
      
      // Check if we should retry
      const shouldRetry = retryCount < maxRetries && 
                         (error.name === 'TimeoutError' || 
                          error.name === 'AbortError' || 
                          error.message.includes('Network Error'));
      
      if (shouldRetry) {
        console.log(`⏳ Retrying quarter conducts in ${retryDelay}ms...`);
        this.setState({ 
          quarterSummaryConductsRetryCount: retryCount + 1,
          quarterSummaryConductsError: `Request failed, retrying in ${retryDelay/1000}s... (${retryCount + 1}/${maxRetries})`
        });
        
        setTimeout(() => {
          this.getQuarterSummaryConducts(retryCount + 1);
        }, retryDelay);
      } else {
        // Final failure - stop retrying
        console.error('💥 Quarter conducts failed after all retries');
        this.setState({
          loadingQuarterSummaryConducts: false,
          quarterSummaryConductsError: `Failed to load admin conducts data after ${maxRetries + 1} attempts. ${error.message}`,
          quarterSummaryConductsRetryCount: 0
        });
      }
    }
  };

  // Manual retry function for quarter summary
  retryQuarterSummary = () => {
    console.log('🔄 Manual retry of quarter summary requested');
    
    // Force clear all caches
    this.cachedData = {};
    if (this.abortControllers.has('quarterSummary')) {
      this.abortControllers.get('quarterSummary').abort();
    }
    
    this.setState({
      quarterSummaryError: null,
      quarterSummaryRetryCount: 0
    });
    
    // Add small delay to let backend reset
    setTimeout(() => {
      this.getQuarterSummary(0);
    }, 500);
  };

  // Manual retry function for quarter conducts
  retryQuarterSummaryConducts = () => {
    console.log('🔄 Manual retry of quarter conducts requested');
    
    // Force clear all caches
    this.cachedData = {};
    if (this.abortControllers.has('quarterConducts')) {
      this.abortControllers.get('quarterConducts').abort();
    }
    
    this.setState({
      quarterSummaryConductsError: null,
      quarterSummaryConductsRetryCount: 0
    });
    
    // Add small delay to let backend reset
    setTimeout(() => {
      this.getQuarterSummaryConducts(0);
    }, 500);
  };

  // Manual retry function for level summary
  retryLevelSummary = () => {
    console.log('🔄 Manual retry of level summary requested');
    
    // Force clear all caches
    this.cachedData = {};
    if (this.abortControllers.has('levelSummary')) {
      this.abortControllers.get('levelSummary').abort();
    }
    
    this.setState({
      levelSummaryError: null,
      levelSummaryRetryCount: 0
    });
    
    // Add small delay to let backend reset
    setTimeout(() => {
      this.getLevelSummary(0);
    }, 500);
  };

  // Optimized progressive loading for admin users with staggered backend calls
  loadAdminDataProgressive = async () => {
    this.setState({ loadingData: true });
    
    try {
      console.log('🚀 Starting staggered admin data loading...');
      
      // Stage 1: Critical data that users see immediately
      console.log('📍 Stage 1: Loading critical admin/level data...');
      await Promise.allSettled([
        this.getSectionAdminSummary(),
        this.getSectionLevelSummary(),
      ]);
      console.log('✅ Stage 1 completed');

      // Stage 2: Delayed level summary (3 second delay to avoid conflicts)
      console.log('📍 Stage 2: Waiting 3 seconds before loading level summary...');
      setTimeout(async () => {
        try {
          console.log('🔄 Starting delayed level summary...');
          await this.getLevelSummary(0);
          console.log('✅ Delayed level summary completed');
        } catch (error) {
          console.error('❌ Error in delayed level summary:', error);
        }
      }, 3000); // 3 second delay

      // Stage 3: Admin grades with extended delay to prevent backend overload
      console.log('📍 Stage 3: Waiting 15 seconds before loading admin grades (backend stability)...');
      setTimeout(async () => {
        try {
          console.log('🔄 Loading admin grades (delayed for backend stability)...');
          await this.getQuarterSummary(0);
          console.log('✅ Admin grades completed');
        } catch (error) {
          console.error('❌ Error loading admin grades:', error);
        }
      }, 15000); // 15 second delay

      // Stage 4: Admin conducts with extended delay (10 seconds after grades)
      setTimeout(async () => {
        try {
          console.log('🔄 Loading admin conducts (delayed for backend stability)...');
          await this.getQuarterSummaryConducts(0);
          console.log('✅ Admin conducts completed');
        } catch (error) {
          console.error('❌ Error loading admin conducts:', error);
        }
      }, 25000); // 25 second delay (10 seconds after grades)

      // Stage 5: Background data (non-blocking, delayed after admin data)
      setTimeout(() => {
        console.log('📍 Stage 5: Loading background data...');
        Promise.allSettled([
          this.getLevelSummaryConducts(),
          this.getClassSummary(),
          this.getSubjectSummary(),
          this.getClassSummaryConducts(),
          this.getSubjectSummaryConducts(),
        ]).catch(error => console.error('Error loading background data:', error));
      }, 30000); // 30 second delay for background data
      
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      this.setState({ loadingData: false });
    }
  };

  // Optimized data refresh based on user type
  refreshDataForUserType = async () => {
    // Prevent multiple simultaneous refreshes
    if (this.state.loadingData) {
      return;
    }

    // Clear cache when refreshing
    this.cachedData = {};

    if (
      this.props.userType === "admin" ||
      this.props.userType === "principal" ||
      this.props.userType === "registrar"
    ) {
      this.setState({ activeButton: "all" });
      await this.loadAdminDataProgressive();
    } else if (this.props.userType === "levelHead") {
      this.setState({ activeButton: "level", loadingData: true });
      try {
        console.log('🚀 Starting levelHead data loading...');
        
        // Stage 1: Load sections immediately
        await Promise.allSettled([
          this.getSectionLevelSummary(),
        ]);
        console.log('✅ Level sections loaded');
        
        // Stage 2: Load level summary grades with 5-second delay
        setTimeout(async () => {
          try {
            console.log('🔄 Loading level summary grades for levelHead...');
            await this.getLevelSummary(0);
            console.log('✅ Level summary grades completed for levelHead');
          } catch (error) {
            console.error('❌ Error loading level summary grades for levelHead:', error);
          }
        }, 5000);

        // Stage 3: Load level summary conducts with 10-second delay (5 seconds after grades)
        setTimeout(async () => {
          try {
            console.log('🔄 Loading level summary conducts for levelHead...');
            await this.getLevelSummaryConducts();
            console.log('✅ Level summary conducts completed for levelHead');
          } catch (error) {
            console.error('❌ Error loading level summary conducts for levelHead:', error);
          }
        }, 10000);
        
        // Stage 4: Background data with longer delay
        setTimeout(() => {
          console.log('📍 Loading background data for levelHead...');
          Promise.allSettled([
            this.getClassSummary(),
            this.getSubjectSummary(),
            this.getClassSummaryConducts(),
            this.getSubjectSummaryConducts(),
          ]).catch(error => console.error('Error loading levelHead background data:', error));
        }, 12000);
        
      } finally {
        this.setState({ loadingData: false });
      }
    } else if (this.props.userType === "adviser") {
      this.setState({ activeButton: "section", loadingData: true });
      try {
        await Promise.allSettled([
          this.getSubjectSummary(),
          this.getClassSummary(),
          this.getSubjectSummaryConducts(),
          this.getClassSummaryConducts(),
        ]);
      } finally {
        this.setState({ loadingData: false });
      }
    } else if (this.props.userType === "teacher") {
      this.setState({ activeButton: "subject", loadingData: true });
      try {
        await Promise.allSettled([
          this.getSubjectSummary(),
          this.getSubjectSummaryConducts(),
        ]);
      } finally {
        this.setState({ loadingData: false });
      }
    } else {
      this.setState({ activeButton: "none" });
    }
  };

  // Optimized school years loading
  getSchoolYears = async () => {
    try {
      const response = await this.makeRequest("/schoolyears?filter[where][active]=true", 'schoolYears', false);
      
      if (response) {
        const currentSchoolYear = _.find(response, {
          isCurrent: true,
        });
        const currentQuarter = getCurrentQuarter();

        // Batch Redux updates
        this.props.quarterActions.setSchoolYearId(currentSchoolYear.schoolYearId);
        this.props.quarterActions.setSchoolYear(currentSchoolYear.schoolYear);
        this.props.quarterActions.setCurrentQuarter(currentQuarter);

        this.setState({
          schoolYears: response,
          currentSchoolYear: currentSchoolYear,
        }, () => {
          // Only refresh if we have the necessary props
          if (this.props.userType && this.props.schoolYearId) {
            this.refreshDataForUserType();
          }
        });
      }
    } catch (error) {
      console.error('Error loading school years:', error);
    }
  };

  // Search functionality
  isSearched(searchKey) {
    return function (item) {
      return (
        !searchKey ||
        item.section.name.toLowerCase().includes(searchKey.toLowerCase()) ||
        item.section.gradeLevel.toLowerCase().includes(searchKey.toLowerCase())
      );
    };
  }

  onSearchChange(event) {
    this.setState({ searchKey: event.target.value });
  }

  // Modal handlers
  openConfirmation = (link, teacherId) =>
    this.setState({
      openConfirm: true,
      link: link,
      teacherId: teacherId,
    });

  closeConfirmation = () =>
    this.setState({
      openConfirm: false,
      link: "",
      teacherId: "",
    });

  handlePaginationChange(e, { activePage }) {
    this.setState({
      activePage: activePage,
    });
  }

  // Optimized change handlers
  handleChangeSchoolYear = (e, { value }) => {
    const selectedSchoolYear = _.find(this.state.schoolYears, {
      schoolYearId: value,
    });
    
    this.props.quarterActions.setSchoolYearId(value);
    this.props.quarterActions.setSchoolYear(selectedSchoolYear.schoolYear);

    // Clear cache and refresh
    this.cachedData = {};
    this.debouncedRefreshData();
  };

  handleChangeQuarter = (e, { value }) => {
    this.props.quarterActions.setCurrentQuarter(value);

    // Clear cache and refresh
    this.cachedData = {};
    this.debouncedRefreshData();
  };

  handleClick = (item) => {
    this.setState({ activeButton: item, searchKey: "" });
  };

  handleNotify = () => {
    const { link, teacherId } = this.state;
    const url = process.env.REACT_APP_URL + "/notifications";
    const data = {
      link: link,
      teacherId: teacherId,
      from: this.props.username,
    };
    axios
      .request({
        method: "post",
        url: url,
        data: data,
      })
      .then((response) => {
        console.log(response.data);
        this.closeConfirmation();
      })
      .catch((err) => console.log(err));
  };

  openToDo = () => {
    console.log("open");
  };

  componentDidMount() {
    this.props.authFn.getToken();
    this.props.onContentMount({ title: "Home" });
    this.getSchoolYears();
    this.getMyClubs();
  }

  /**
   * The clubs that want something from this user, and what.
   *
   * Not only the ones they moderate: a club's marks climb the same ladder a
   * subject's grades do, so a level head, registrar and principal each have a
   * rung of their own, and an administrator stands in for every rung. Asking
   * only "which clubs do I moderate?" left everyone above the moderator with an
   * empty dashboard and thirteen clubs to open one by one to find the work.
   *
   * Whose move it is comes from utils/clubWorkflow, shared with the club's own
   * approval bar and the status column on the club list, so the three cannot
   * disagree about who is being waited on.
   */
  getMyClubs = async () => {
    const { userId, schoolYearId, currentQuarter, schoolYear, userType } = this.props;
    if (!schoolYearId || !currentQuarter) return;

    const clubsRes = await fetchData(
      `/clubs?filter[where][active]=true&filter[where][schoolYearId]=${schoolYearId}`
    );
    if (!clubsRes || clubsRes.data.length === 0) return this.setState({ myClubs: [] });

    const quarter = parseInt(currentQuarter, 10);

    // Narrowed to this year's clubs rather than asking for every membership and
    // every workflow row there has ever been. Today that is a handful either
    // way, but this runs on every dashboard load for every user with a part in
    // a club, and it would grow by a school year every year - on a backend that
    // already sheds load deliberately, that is the wrong direction to leave it.
    const scope = clubsRes.data
      .map((c) => `filter[where][clubId][inq][]=${c.clubId}`)
      .join("&");

    const [membersRes, detailsRes] = await Promise.all([
      fetchData(`/clubStudents?filter[where][active]=true&${scope}`),
      fetchData(
        `/clubDetails?filter[where][active]=true&filter[where][type]=grade` +
          `&filter[where][quarter]=${quarter}&${scope}`
      ),
    ]);

    const members = membersRes ? membersRes.data : [];
    const byClub = {};
    if (detailsRes) detailsRes.data.forEach((d) => (byClub[d.clubId] = d));

    const scheme = resolveGradingScheme(schoolYear, null);
    const period =
      scheme.periods.filter((p) => p.index === quarter)[0] || scheme.periods[0];

    // A member counts as marked if either column carries one: a club crosses
    // grade levels, so a Grade 1 member is rated in letters while the Grade 5
    // beside them takes a number.
    const marked = (row) => {
      if (!period) return false;
      const letter = period.letterField ? row[period.letterField] : "";
      return Number(row[period.dbField]) > 0 || Boolean(letter && String(letter).trim());
    };

    const rows = clubsRes.data.map((club) => {
      const mine = members.filter((m) => m.clubId === club.clubId);
      const detail = byClub[club.clubId];
      const status = (detail && detail.status) || "To Do";
      const isModerator = Boolean(club.teacherId) && club.teacherId === userId;

      return {
        clubId: club.clubId,
        name: club.name,
        members: mine.length,
        unmarked: mine.filter((m) => !marked(m)).length,
        status: status,
        isModerator: isModerator,
        awaits: awaitsUser(status, { userType: userType, isModerator: isModerator }),
      };
    });

    /*
     * Every club, not only the ones outstanding for this user - the subject
     * cards count every subject across all five statuses, and clubs now read
     * the same way, so a level head can see what is coming as well as what is
     * theirs today. Which rows are actually their move is marked inside.
     *
     * The section is hidden altogether from someone with no part in any club:
     * a subject teacher who moderates none has no business being shown
     * thirteen of them.
     */
    const approves =
      ["admin", "levelHead", "registrar", "principal"].indexOf(userType) !== -1;
    const hasPart = approves || rows.some((r) => r.isModerator);

    this.setState({ myClubs: hasPart ? rows : [] });
  };

  componentDidUpdate(prevProps) {
    // Only refresh if relevant props actually changed
    const shouldRefresh = (
      (prevProps.schoolYearId !== this.props.schoolYearId && this.props.schoolYearId) ||
      (prevProps.currentQuarter !== this.props.currentQuarter && this.props.currentQuarter) ||
      (prevProps.userType !== this.props.userType && this.props.userType)
    );

    console.log('🔄 componentDidUpdate check:', {
      shouldRefresh,
      loadingData: this.state.loadingData,
      prevProps: {
        schoolYearId: prevProps.schoolYearId,
        currentQuarter: prevProps.currentQuarter,
        userType: prevProps.userType
      },
      currentProps: {
        schoolYearId: this.props.schoolYearId,
        currentQuarter: this.props.currentQuarter,
        userType: this.props.userType
      }
    });

    if (shouldRefresh && !this.state.loadingData) {
      console.log('🚀 Triggering data refresh due to props change');
      // Add small delay to ensure props are fully updated
      setTimeout(() => {
        this.refreshDataForUserType();
        // The club card counts marks for the selected term, so it has to follow
        // the term selector as well as the school year.
        this.getMyClubs();
      }, 100);
    }
  }

  componentWillUnmount() {
    // Cancel all pending requests
    this.abortControllers.forEach(controller => {
      try {
        controller.abort();
      } catch (error) {
        console.warn('Error aborting request:', error);
      }
    });
    
    // Clear any pending timeouts
    if (this.debouncedRefreshData && typeof this.debouncedRefreshData.cancel === 'function') {
      this.debouncedRefreshData.cancel();
    }

    // Clear cache
    this.cachedData = null;
  }

  render() {
    return (
      <HomePV
        auth={this.props.auth}
        userType={this.props.userType}
        levelType={this.props.levelType}
        perSubjectSummary={this.state.perSubjectSummary}
        perSectionSummary={this.state.perSectionSummary}
        perLevelSummary={this.state.perLevelSummary}
        perQuarterSummary={this.state.perQuarterSummary}
        perSubjectSummaryConducts={this.state.perSubjectSummaryConducts}
        perSectionSummaryConducts={this.state.perSectionSummaryConducts}
        perLevelSummaryConducts={this.state.perLevelSummaryConducts}
        perQuarterSummaryConducts={this.state.perQuarterSummaryConducts}
        quarter={this.props.currentQuarter}
        loadedSubject={this.state.loadedSubject}
        loadedClass={this.state.loadedClass}
        loadedLevel={this.state.loadedLevel}
        loadedQuarter={this.state.loadedQuarter}
        schoolYearId={this.props.schoolYearId}
        schoolYears={this.state.schoolYears}
        activeButton={this.state.activeButton}
        myClubs={this.state.myClubs}
        details={this.state.details}
        detailsAdmin={this.state.detailsAdmin}
        searchKey={this.state.searchKey}
        activePage={this.state.activePage}
        itemPerPage={this.state.itemPerPage}
        openConfirm={this.state.openConfirm}
        isSearched={this.isSearched}
        onSearchChange={this.onSearchChange}
        openConfirmation={this.openConfirmation}
        closeConfirmation={this.closeConfirmation}
        handleNotify={this.handleNotify}
        handlePaginationChange={this.handlePaginationChange}
        handleChangeSchoolYear={this.handleChangeSchoolYear}
        handleChangeQuarter={this.handleChangeQuarter}
        handleClick={this.handleClick}
        openToDo={this.openToDo}
        loadingAdmin={this.state.loadingAdmin}
        loadingLevel={this.state.loadingLevel}
        loadingData={this.state.loadingData}
        loadingQuarterSummary={this.state.loadingQuarterSummary}
        quarterSummaryError={this.state.quarterSummaryError}
        quarterSummaryRetryCount={this.state.quarterSummaryRetryCount}
        retryQuarterSummary={this.retryQuarterSummary}
        loadingQuarterSummaryConducts={this.state.loadingQuarterSummaryConducts}
        quarterSummaryConductsError={this.state.quarterSummaryConductsError}
        quarterSummaryConductsRetryCount={this.state.quarterSummaryConductsRetryCount}
        retryQuarterSummaryConducts={this.retryQuarterSummaryConducts}
        loadingLevelSummary={this.state.loadingLevelSummary}
        levelSummaryError={this.state.levelSummaryError}
        levelSummaryRetryCount={this.state.levelSummaryRetryCount}
        retryLevelSummary={this.retryLevelSummary}
      />
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    authFn: AuthA(dispatch),
    quarterActions: QuarterA(dispatch),
  };
};

const mapStateToProps = (state) => {
  return {
    auth: state.AuthR.auth,
    userType: state.AuthR.userType,
    userId: state.AuthR.userId,
    levelType: state.AuthR.levelType,
    username: state.AuthR.username,
    currentQuarter: state.QuarterR.currentQuarter,
    schoolYear: state.QuarterR.schoolYear,
    schoolYearId: state.QuarterR.schoolYearId,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(HomePC);